import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useRecoilValue } from 'recoil';
import { Box, CircularProgress } from '@mui/material';

import { authState, userState } from '@recoils/atoms';
import { isAdminUserGroup } from '@models/user';

/**
 * AdminRoute
 * - Allows access only to authenticated users who belong to an admin group.
 * - Shows a loader while auth state is being resolved to avoid login flicker.
 * - Preserves the intended destination via `returnTo` when redirecting to Login.
 */
function AdminRoute() {
  const isAuthenticated = useRecoilValue(authState);
  const user = useRecoilValue(userState);
  const location = useLocation();

  const returnTo = encodeURIComponent(location.pathname + location.search);

  // Auth state is not resolved yet. Show a loader to prevent redirect flicker.
  if (isAuthenticated === null) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Not authenticated (or user is missing): redirect to Login.
  // NOTE: user should be present if auth is true, because Root loader sets recoil on mount.
  if (isAuthenticated === false || !user) {
    return (
      <Navigate
        to={`/login?reason=session_expired&returnTo=${returnTo}`}
        replace
      />
    );
  }

  // Role guard: allow only internal admin groups.
  // If a partner tries to access an admin-only route, redirect to a safe default.
  // (You can replace this with a dedicated 403 page later.)
  if (!isAdminUserGroup(user.group)) {
    return <Navigate to="/orders" replace />;
  }

  return <Outlet />;
}

export default AdminRoute;
